// The location hash is the shareable state: enabled layers, filters, selection and
// viewport. Writes are debounced replaceState; external changes (back/forward,
// pasted link) re-parse.
import { batch, effect } from "@preact/signals";
import type { LayerId, SortKey } from "../types";
import { LAYERS, LAYER_ORDER } from "../data/layers";
import {
  access, centerMode, cities, counties, dateFrom, dateTo, dishes, enabledLayers, eventBands, eventCats, eventFeeds, eventFrom, eventTo, eventsOpen, facilities, featuresByKey, focusBBox, followMap,
  freeOnly, kinds, layerLoaded, mapBounds, meals, noLivestock, periods, q, radiusKm, savedOnly, selectedKey, sort, typeGroups, wheelchair,
} from "./store";

let writing = false;
let viewportFromURL: { c: [number, number]; z: number } | null = null;
/** True once the app has applied the initial URL (so effects don't clobber it). */
let ready = false;

const csv = (s: Set<string>) => [...s].join(",");
const fromCsv = (v: string | null) => new Set((v ?? "").split(",").filter(Boolean));

export function encodeState(): string {
  const p = new URLSearchParams();
  const on = LAYER_ORDER.filter((id) => enabledLayers.value.has(id));
  const dflt = LAYER_ORDER.filter((id) => LAYERS[id].defaultOn);
  if (on.join() !== dflt.join()) p.set("layers", on.join(","));
  if (q.value) p.set("q", q.value);
  if (dateFrom.value != null) p.set("from", String(dateFrom.value));
  if (dateTo.value != null) p.set("to", String(dateTo.value));
  if (periods.value.size) p.set("periods", csv(periods.value));
  if (access.value.size) p.set("access", csv(access.value));
  if (facilities.value.size) p.set("fac", csv(facilities.value));
  if (typeGroups.value.size) p.set("kind", csv(typeGroups.value));
  if (noLivestock.value) p.set("nolivestock", "1");
  if (freeOnly.value) p.set("free", "1");
  if (wheelchair.value) p.set("wheelchair", "1");
  for (const [layer, set] of Object.entries(kinds.value)) if (set.size) p.set(`${layer[0]}k`, csv(set));
  if (dishes.value.size) p.set("dish", csv(dishes.value));
  if (meals.value.size) p.set("meal", csv(meals.value));
  if (counties.value.size) p.set("county", csv(counties.value));
  if (radiusKm.value) p.set("r", String(radiusKm.value));
  const c = centerMode.value;
  if (c && c.kind === "city") p.set("city", String(c.idx)); // "me" is not shareable
  if (savedOnly.value) p.set("saved", "1");
  if (eventsOpen.value) p.set("events", "1");
  if (eventFrom.value) p.set("ef", eventFrom.value);
  if (eventTo.value) p.set("et", eventTo.value);
  if (eventFeeds.value.size) p.set("evf", csv(eventFeeds.value));
  if (eventCats.value.size) p.set("evc", csv(eventCats.value));
  if (eventBands.value.size) p.set("evb", csv(eventBands.value));
  if (!followMap.value) p.set("follow", "0");
  if (sort.value !== "name") p.set("sort", sort.value);
  if (selectedKey.value) p.set("sel", selectedKey.value);
  const b = mapBounds.value;
  if (b) p.set("bbox", b.map((x) => x.toFixed(3)).join(","));
  return p.toString();
}

function writeHash(): void {
  if (!ready) return;
  const next = encodeState();
  const target = next ? `#${next}` : location.pathname + location.search;
  if ((location.hash || "") === (next ? `#${next}` : "")) return;
  writing = true;
  history.replaceState(null, "", target);
  setTimeout(() => { writing = false; }, 0);
}

let timer: ReturnType<typeof setTimeout> | undefined;
function scheduleWrite(): void {
  clearTimeout(timer);
  timer = setTimeout(writeHash, 250);
}

export function readHash(): void {
  const raw = (location.hash || "").replace(/^#/, "");
  const p = new URLSearchParams(raw);
  batch(() => {
    if (p.has("layers")) {
      const set = new Set<LayerId>();
      for (const v of p.get("layers")!.split(",")) if ((LAYER_ORDER as string[]).includes(v)) set.add(v as LayerId);
      if (set.size) enabledLayers.value = set;
    }
    q.value = p.get("q") ?? "";
    const num = (k: string) => { const n = Number(p.get(k)); return p.has(k) && Number.isFinite(n) ? n : null; };
    dateFrom.value = num("from");
    dateTo.value = num("to");
    periods.value = fromCsv(p.get("periods"));
    access.value = fromCsv(p.get("access"));
    facilities.value = fromCsv(p.get("fac"));
    typeGroups.value = fromCsv(p.get("kind"));
    noLivestock.value = p.get("nolivestock") === "1";
    freeOnly.value = p.get("free") === "1";
    wheelchair.value = p.get("wheelchair") === "1";
    kinds.value = { nature: fromCsv(p.get("nk")), gaa: fromCsv(p.get("gk")), equine: fromCsv(p.get("ek")), food: fromCsv(p.get("fk")), trains: fromCsv(p.get("tk")) };
    dishes.value = fromCsv(p.get("dish"));
    meals.value = fromCsv(p.get("meal"));
    counties.value = fromCsv(p.get("county"));
    const r = num("r");
    radiusKm.value = r && r > 0 ? r : null;
    const city = num("city");
    centerMode.value = city != null && city >= 0 ? { kind: "city", idx: city } : null;
    savedOnly.value = p.get("saved") === "1";
    // "matches=1" and mc/ml are the pre-equine links; they still open the same panel.
    eventsOpen.value = p.get("events") === "1" || p.get("matches") === "1";
    const iso = (k: string) => (/^\d{4}-\d{2}-\d{2}$/.test(p.get(k) ?? "") ? p.get(k) : null);
    eventFrom.value = iso("ef");
    eventTo.value = iso("et");
    eventFeeds.value = fromCsv(p.get("evf"));
    const legacy = (key: string, feed: string) => new Set([...fromCsv(p.get(key))].map((v) => `${feed}:${v}`));
    eventCats.value = p.has("evc") ? fromCsv(p.get("evc")) : legacy("mc", "gaa");
    eventBands.value = p.has("evb") ? fromCsv(p.get("evb")) : legacy("ml", "gaa");
    followMap.value = p.get("follow") !== "0";
    const s = p.get("sort");
    sort.value = (["relevance", "name", "date_asc", "date_desc", "distance"] as SortKey[]).includes(s as SortKey) ? (s as SortKey) : "name";
    // Selection: new "sel=layer:id" and legacy "site=id" links.
    const sel = p.get("sel") ?? (p.has("site") ? `history:${p.get("site")}` : null);
    selectedKey.value = sel;
    if (sel) {
      const layer = sel.split(":")[0] as LayerId;
      if ((LAYER_ORDER as string[]).includes(layer) && !enabledLayers.value.has(layer)) {
        enabledLayers.value = new Set([...enabledLayers.value, layer]);
      }
    }
    viewportFromURL = null;
    if (p.has("bbox")) {
      const b = p.get("bbox")!.split(",").map(Number);
      if (b.length === 4 && b.every(Number.isFinite)) viewportFromURL = { c: [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2], z: 0 };
      if (viewportFromURL) focusBBox([b[0], b[1], b[2], b[3]]);
    } else if (p.has("c") && p.has("z")) {
      // legacy v1 links: c=lat,lng
      const [lat, lng] = p.get("c")!.split(",").map(Number);
      const z = Number(p.get("z"));
      if ([lat, lng, z].every(Number.isFinite)) {
        const span = 360 / Math.pow(2, z) * 0.6;
        focusBBox([lng - span / 2, lat - span / 4, lng + span / 2, lat + span / 4]);
      }
    }
  });
}

export function initFromURL(): void {
  readHash();
  ready = true;
  // A selected feature in the URL should also be flown to once data has arrived.
  // (effect() runs synchronously on creation, so `stop` can't be called from the
  // first run — track completion with a flag instead.)
  let done = false;
  let stop: (() => void) | undefined;
  stop = effect(() => {
    if (done) return;
    const key = selectedKey.value;
    if (!key) { done = true; stop?.(); return; }
    const layer = key.split(":")[0] as LayerId;
    if (!layerLoaded.value[layer]) return;
    const f = featuresByKey.value.get(key);
    if (f && !viewportFromURL) focusBBox([f.lon - 0.05, f.lat - 0.03, f.lon + 0.05, f.lat + 0.03]);
    done = true;
    stop?.();
  });
  if (done) stop();
  // City index is only meaningful once cities are loaded; nothing to do here — the
  // computed radiusCenter resolves lazily.
  void cities;
}

export function startURLSync(): void {
  effect(() => {
    encodeState(); // subscribe to everything the encoder reads
    scheduleWrite();
  });
  window.addEventListener("hashchange", () => {
    if (writing) return;
    readHash();
  });
}
